import React, { useState, useEffect, useMemo } from "react";
import "./Dashboard.scss";
import ChemIndexDashboardBarChart from "./feature/ChemIndexDashboardBarChart";
import ChemIndexDashboardGrowthChart from "./feature/ChemIndexDashboardGrowthChart";
import {
  useGetChemIndexDashboardCountsDetilsQuery,
  useGetChemProductsAvailableYearsQuery,
} from "../../app/services/dashboardAPI";

// 🔢 Animated Counter Component
function AnimatedCounter({ value, duration = 1500 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        clearInterval(timer);
        setCount(value);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{count.toLocaleString()}</span>;
}

const Dashboard = () => {
  const { isFetching, isSuccess, data } = useGetChemIndexDashboardCountsDetilsQuery();

  // Fetch available years for growth chart
  const { data: availableYearsData = [], isFetching: isFetchingYears } =
    useGetChemProductsAvailableYearsQuery();

  const years = useMemo(() => {
    if (!availableYearsData || availableYearsData.length === 0) return [];
    return availableYearsData.map((y) => y.year).sort();
  }, [availableYearsData]);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Update selectedYear when available years load
  useEffect(() => {
    if (years.length && !years.includes(selectedYear)) {
      setSelectedYear(years[years.length - 1]);
    }
  }, [years, selectedYear]);

  const getValueByLabel = (label) => {
    if (!isSuccess || !data) return 0;
    const item = data.find((d) => d.label === label);
    return item ? item.value : 0;
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1 className="dashboard-title">Dashboard Overview</h1>
        <p className="dashboard-subtitle">Real-time insights and chemical product statistics</p>
      </header>

      {/* Summary Cards */}
      <div className="dashboard-cards">
        {/* Products Card */}
        <div className="summary-card card-blue">
          <div className="card-badge">LIVE</div>
          <div className="card-icon">
            <i className="bi bi-box"></i>
          </div>
          <div className="card-content">
            <div className="card-label">Total Products</div>
            <div className="card-value">
              {isFetching ? "..." : <AnimatedCounter value={getValueByLabel("ChemProducts")} />}
            </div>
          </div>
        </div>

        {/* Hazards Card */}
        <div className="summary-card card-organe">
          <div className="card-badge">ACTIVE</div>
          <div className="card-icon">
            <i className="bi bi-shield-fill-exclamation"></i>
          </div>
          <div className="card-content">
            <div className="card-label">Total Salts</div>
            <div className="card-value">
              {isFetching ? "..." : <AnimatedCounter value={getValueByLabel("Salts")} />}
            </div>
          </div>
        </div>

        {/* Synonyms Card */}
        <div className="summary-card card-purple">
          <div className="card-badge">UPDATED</div>
          <div className="card-icon">
            <i className="bi bi-fonts"></i>
          </div>
          <div className="card-content">
            <div className="card-label">Total StereoIsomers</div>
            <div className="card-value">
              {isFetching ? "..." : <AnimatedCounter value={getValueByLabel("StereoIsomers")} />}
            </div>
          </div>
        </div>

        {/* Tautomers Card */}
        <div className="summary-card card-green">
          <div className="card-badge">STABLE</div>
          <div className="card-icon">
            <i className="bi bi-diagram-3-fill"></i>
          </div>
          <div className="card-content">
            <div className="card-label">Tautomers</div>
            <div className="card-value">
              {isFetching ? "..." : <AnimatedCounter value={getValueByLabel("Tautomers")} />}
            </div>
          </div>
        </div>
      </div>

      {/* Product Overview Bar Chart */}
      <div className="dashboard-charts-grid mb-10">
        <div className="chart-item">
          <div className="chart-header">
            <h3 className="chart-title">Product Distribution Breakdown</h3>
          </div>
          <ChemIndexDashboardBarChart
            isGetDashboardCountsFetching={isFetching}
            data={data || []}
          />
        </div>
      </div>

      {/* ChemIndex Monthly Growth Chart */}
      <div className="dashboard-charts-grid">
        <div className="chart-item">
          <div className="chart-header">
            <h3 className="chart-title">Monthly Growth Statistics</h3>
            {!isFetchingYears && (
              <div className="year-selector">
                <label>Period:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {years.map((yr) => (
                    <option key={yr} value={yr}>
                      FY {yr}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {isFetchingYears ? (
            <div className="spinner-wrapper">Loading growth data...</div>
          ) : (
            <ChemIndexDashboardGrowthChart selectedYear={selectedYear} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
